import { Download, LoaderCircle, Send } from "lucide-react";
import { FormEvent, useState } from "react";
import type { DatabookQaQuestion } from "@/lib/api/databook";
import { DatabookInspectorPanel, type InspectorPanelProps } from "./DatabookInspectorPanel";

const priorityClass = {
  High: "text-red-700 dark:text-red-400",
  Medium: "text-amber-700 dark:text-amber-400",
  Low: "text-muted-foreground",
};

export function DatabookQnaPanel({
  question, onDownload, downloading = false, downloadError, ...props
}: InspectorPanelProps & { question: DatabookQaQuestion | null; onDownload?: () => void; downloading?: boolean; downloadError?: string | null }) {
  const [reply, setReply] = useState("");
  const submitReply = (event: FormEvent) => {
    event.preventDefault();
    const value = reply.trim();
    if (!value) return;
    setReply("");
  };

  return <DatabookInspectorPanel {...props} kind="qna" actionError={downloadError} headerAction={onDownload && (
    <button type="button" onClick={onDownload}
      disabled={props.loading || props.error || !props.total || downloading}
      title="Download all Q&A as Excel"
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
      {downloading ? <LoaderCircle aria-hidden="true" size={14} className="animate-spin" /> : <Download aria-hidden="true" size={14} />}
      {downloading ? "Downloading…" : "Excel"}
    </button>
  )} footerContent={question && (
    <form onSubmit={submitReply} className="shrink-0 border-t border-border bg-card p-4">
      <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-foreground">Management</label>
      <div className="flex items-end gap-2 rounded-xl border border-border bg-background p-2">
        <textarea value={reply} onChange={(event) => setReply(event.target.value)} rows={2} placeholder="Reply to this question…" aria-label="Reply to Q&A thread" className="min-h-10 flex-1 resize-none bg-transparent px-2 py-1 text-sm outline-none placeholder:text-muted-foreground" />
        <button type="submit" aria-label="Send reply" disabled={!reply.trim()} className="rounded-lg bg-foreground p-2 text-background disabled:opacity-40"><Send aria-hidden="true" size={14} /></button>
      </div>
    </form>
  )}>
    {question && (
      <section className="space-y-4 border-b border-border py-5 last:border-0">
        <div className="flex items-center justify-between gap-2 text-xs font-semibold">
          <span>Question {props.position + 1}</span>
          <span className={priorityClass[question.priority]}>{question.priority} priority</span>
        </div>
        <div className="space-y-1 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
          <p className="font-medium text-foreground/80">{question.area}</p>
          <p>{question.period}</p>
          {question.value !== null && <p>Value: {question.value.toLocaleString("en-US", { maximumSignificantDigits: 15 })}</p>}
          {question.variance !== null && <p>Variance: {question.variance.toLocaleString("en-US", { maximumSignificantDigits: 15 })}</p>}
        </div>
        <div className="rounded-xl border border-border bg-muted/50 p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-foreground">Advisor</p>
            <p className="whitespace-pre-wrap break-words text-[13px] leading-[1.65] text-foreground/90 [overflow-wrap:anywhere]">{question.question}</p>
        </div>
      </section>
    )}
  </DatabookInspectorPanel>;
}
